import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { ShoppingBagIcon, TruckIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, total, clearCart } = useCart();
  const { settings } = useSettings();
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [transferReceipt, setTransferReceipt] = useState(null);
  const [transferReceiptPreview, setTransferReceiptPreview] = useState(null);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: '',
      city: '',
      notes: ''
    }
  });

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        toast.error('حجم الملف يجب أن يكون أقل من 5 ميجابايت');
        return;
      }
      setTransferReceipt(file);
      setTransferReceiptPreview(URL.createObjectURL(file));
    }
  };

  const createOrderMutation = useMutation(
    (orderData) => {
      const formData = new FormData();
      
      // Add order data
      formData.append('items', JSON.stringify(orderData.items));
      formData.append('shipping_address', orderData.shipping_address);
      formData.append('phone', orderData.phone);
      formData.append('payment_method', orderData.payment_method);
      if (orderData.notes) {
        formData.append('notes', orderData.notes);
      }
      
      // Add transfer receipt if bank transfer is selected
      if (orderData.payment_method === 'bank_transfer' && transferReceipt) {
        formData.append('transfer_receipt', transferReceipt);
      }
      
      return api.post('/orders', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: (response) => {
        clearCart();
        toast.success('تم إنشاء الطلب بنجاح');
        navigate('/orders');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'حدث خطأ في إنشاء الطلب');
      }
    }
  );

  const onSubmit = (data) => {
    if (cartItems.length === 0) {
      toast.error('السلة فارغة');
      return;
    }

    if (paymentMethod === 'bank_transfer' && !transferReceipt) {
      toast.error('يرجى إرفاق إشعار التحويل');
      return;
    }

    const orderData = {
      items: cartItems.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        price: item.product.price
      })),
      shipping_address: `${data.address}, ${data.city}`,
      phone: data.phone,
      payment_method: paymentMethod,
      notes: data.notes || ''
    };

    createOrderMutation.mutate(orderData);
  };

  if (cartItems.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-16">
          <ShoppingBagIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">السلة فارغة</h2>
          <p className="text-gray-600 mb-6">أضف منتجات للسلة لإتمام عملية الشراء</p>
          <button
            onClick={() => navigate('/products')}
            className="btn-primary"
          >
            تصفح المنتجات
          </button>
        </div>
      </div>
    );
  }

  const shippingCost = parseFloat(settings?.business?.shipping_cost || 50);
  const taxRate = parseFloat(settings?.business?.tax_rate || 0) / 100;
  const subtotal = total;
  const tax = subtotal * taxRate;
  const totalAmount = subtotal + shippingCost + tax;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">إتمام الشراء</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Shipping & Payment Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Shipping Address */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <TruckIcon className="w-6 h-6 text-primary-600 ml-2" />
              <h2 className="text-xl font-semibold">معلومات الشحن</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الاسم الكامل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'الاسم مطلوب' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  {...register('phone', { required: 'رقم الهاتف مطلوب' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المدينة <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('city', { required: 'المدينة مطلوبة' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان التفصيلي <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('address', { required: 'العنوان مطلوب' })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="الشارع، رقم المنزل، معالم قريبة..."
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ملاحظات إضافية (اختياري)
                </label>
                <textarea
                  {...register('notes')}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="أي ملاحظات خاصة بالطلب..."
                />
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <CreditCardIcon className="w-6 h-6 text-primary-600 ml-2" />
              <h2 className="text-xl font-semibold">طريقة الدفع</h2>
            </div>

            <div className="space-y-3">
              <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                <input
                  type="radio"
                  value="cash_on_delivery"
                  checked={paymentMethod === 'cash_on_delivery'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-4 h-4 text-primary-600"
                />
                <div className="mr-3">
                  <p className="font-medium">الدفع عند الاستلام</p>
                  <p className="text-sm text-gray-600">ادفع نقداً عند استلام الطلب</p>
                </div>
              </label>

              <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                <input
                  type="radio"
                  value="bank_transfer"
                  checked={paymentMethod === 'bank_transfer'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-4 h-4 text-primary-600"
                />
                <div className="mr-3">
                  <p className="font-medium">تحويل بنكي</p>
                  <p className="text-sm text-gray-600">قم بالتحويل وإرفاق الإشعار</p>
                </div>
              </label>

              {/* Bank Transfer Details */}
              {paymentMethod === 'bank_transfer' && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-3">تفاصيل الحساب البنكي</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">اسم البنك:</span>
                      <span className="text-gray-900">بنك الخرطوم</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">رقم الحساب:</span>
                      <span className="text-gray-900 font-mono">1234567890</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">اسم صاحب الحساب:</span>
                      <span className="text-gray-900">أمازول للتجارة الإلكترونية</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">المبلغ المطلوب:</span>
                      <span className="text-primary-600 font-bold">{totalAmount.toLocaleString()} جنيه</span>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-800">
                      ⚠️ يرجى التحويل بالمبلغ الكامل وإرفاق إشعار التحويل أدناه
                    </p>
                  </div>

                  {/* Upload Receipt */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      إرفاق إشعار التحويل <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-500 transition-colors">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={handleReceiptChange}
                            className="hidden"
                          />
                          <div className="text-sm text-gray-600">
                            {transferReceipt ? (
                              <span className="text-primary-600 font-medium">
                                ✓ {transferReceipt.name}
                              </span>
                            ) : (
                              <>
                                <span className="font-medium">اضغط لاختيار الملف</span>
                                <br />
                                <span className="text-xs">صورة أو PDF (حد أقصى 5MB)</span>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>

                    {transferReceiptPreview && (
                      <div className="mt-3">
                        <img
                          src={transferReceiptPreview}
                          alt="معاينة الإشعار"
                          className="max-w-xs rounded-lg border"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-4">
            <h2 className="text-xl font-semibold mb-4">ملخص الطلب</h2>

            {/* Cart Items */}
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {cartItems.map((item) => (
                <div key={item.product.id} className="flex items-center gap-3 pb-3 border-b">
                  {item.product.images?.[0]?.url && (
                    <img
                      src={item.product.images[0].url}
                      alt={item.product.name}
                      className="w-16 h-16 rounded object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.product.name}</p>
                    <p className="text-sm text-gray-600">
                      {item.quantity} × {item.product.price} جنيه
                    </p>
                  </div>
                  <p className="font-medium">
                    {(item.quantity * item.product.price).toLocaleString()} جنيه
                  </p>
                </div>
              ))}
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">المجموع الفرعي</span>
                <span className="font-medium">{subtotal.toLocaleString()} جنيه</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">الشحن</span>
                <span className="font-medium">{shippingCost.toLocaleString()} جنيه</span>
              </div>
              {tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">الضريبة ({(taxRate * 100).toFixed(0)}%)</span>
                  <span className="font-medium">{tax.toLocaleString()} جنيه</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                <span>الإجمالي</span>
                <span className="text-primary-600">{totalAmount.toLocaleString()} جنيه</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={createOrderMutation.isLoading}
              className="w-full mt-6 btn-primary disabled:opacity-50"
            >
              {createOrderMutation.isLoading ? 'جاري إنشاء الطلب...' : 'تأكيد الطلب'}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              بالضغط على "تأكيد الطلب" فإنك توافق على شروط وأحكام الموقع
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
